import AWSAppSyncClient, { AUTH_TYPE } from "aws-appsync";
import { Auth } from "@aws-amplify/auth";
import { GraphqlClient, MemoisedSchemaClient, SchemaClient } from "@digitallab/grid-common-lib";

/**
 * Read client the schema resolves its master data through.
 *
 * Points at the **instrument repository** API. This app talks to several AppSync endpoints, and the
 * equipment master data lives on this one: every source the schema reads
 * (`listIRCategoryMappings`, `listSupportedLinkedInstances`, `listIRFilterLookups`,
 * `listIRGroupMasters`, `listIRSOPMasters`) is issued with `awsAppSyncConfig: REPO_CONFIG` in
 * `LoadEquipmentService`. Booking (`REACT_APP_AWS_GRAPHQL_ENDPOINT`) does not expose them, and the
 * landing-page endpoint serves only user details here — pointing at either fails every read, which
 * leaves the derived column data empty and every schema-only column missing from the grid.
 *
 * instrument-repository is itself that API's app, so its own client reads
 * `REACT_APP_AWS_GRAPHQL_ENDPOINT` and means this same API — the variable names do not transfer.
 *
 * `cache-first` because every caller wants the same master data and reads it repeatedly;
 * `MemoisedSchemaClient` on top guarantees one read per source per client regardless of the Apollo
 * cache policy, since several fields resolve their filter values from the same query.
 *
 * Deliberately in its own module, not imported from `constants.js`: `aws-appsync` pulls in
 * `@aws-sdk/lib-storage` and `@aws-sdk/client-s3`, whose `node:` specifiers this jest version
 * cannot resolve. `constants.js` is reachable from most of the test suite, so it must import only
 * the synchronous cache getters and leave this behind the async loader.
 *
 * @returns A memoised, repository-backed schema client.
 */
export const makeSchemaClient = (): SchemaClient =>
  new MemoisedSchemaClient(
    new GraphqlClient(
      new AWSAppSyncClient({
        url: process.env.REACT_APP_REPO_AWS_GRAPHQL_ENDPOINT ?? "",
        region: process.env.REACT_APP_AWS_PROJECT_REGION ?? "eu-central-1",
        auth: {
          type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
          jwtToken: async () => (await Auth.currentSession()).getAccessToken().getJwtToken()
        },
        disableOffline: true
      }),
      "cache-first"
    )
  );
